import React, {
  useCallback,
  useLayoutEffect,
  useRef,
  useState
} from "react"

const offset = 5

const VirtualList = (props) => {
  const {
    next,
    data,
    keyField,
    hasMore,
    className,
    itemHeight,
    renderItem,
    dataLength = 0,
    listHeaderComponent,
    listFooterComponent,
    listEmptyComponent,
    loader = null
  } = props

  const [items, setItems] = useState([])
  const [padding, setPadding] = useState({ top: 0, bottom: 0 })

  const containerRef = useRef(null)
  const wrapperRef = useRef(null)
  const previousStartRow = useRef()
  const previousDataLength = useRef()

  const handleResize =
    useCallback(() => {
      if (!containerRef.current) return

      previousStartRow.current = undefined

      showViewableItems()

      listenToScrollProgress()
    }, [
      data,
      dataLength,
      hasMore
    ])

  const showViewableItems =
    useCallback(() => {
      if (data.length === 0) return

      const top =
        containerRef.current.scrollTop -
        (wrapperRef.current.offsetTop - containerRef.current.offsetTop)


      let startRow =
        Math.max(((top / itemHeight) | 0), 0)

      const viewPortHeight =
        containerRef.current.clientHeight

      const totalRows = data.length

      if (previousStartRow.current === startRow) return
      else previousStartRow.current = startRow

      const numberOfRows = Math.ceil(viewPortHeight / itemHeight)

      const endRow =
        Math.min(startRow + numberOfRows + offset, totalRows)

      startRow =
        Math.max(startRow - offset, 0)

      const result =
        data.slice(startRow, endRow)

      setItems(result)

      setPadding({
        top: startRow * itemHeight,
        bottom: (totalRows - endRow) * itemHeight
      })
    }, [data])

  const listenToScrollProgress =
    useCallback(() => {
      if (!next) return

      if (!hasMore) return

      if (data.length === 0) return

      const winScroll = containerRef.current.scrollTop

      const height =
        containerRef.current.scrollHeight -
        containerRef.current.clientHeight

      if (winScroll + 100 < height) return

      if (previousDataLength.current === dataLength) return

      previousDataLength.current = dataLength

      next()
    }, [
      data,
      next,
      hasMore,
      dataLength
    ])

  const handleScroll =
    useCallback(() => {
      showViewableItems()

      listenToScrollProgress()
    }, [
      data,
      next,
      hasMore,
      dataLength
    ])

  const initialize = () => {
    previousStartRow.current = undefined

    showViewableItems()

    setTimeout(() => {
      listenToScrollProgress()
    }, 5)
  }

  useLayoutEffect(() => {
    initialize()
  }, [data])

  useLayoutEffect(() => {
    const observer = new ResizeObserver(handleResize)

    containerRef.current &&
      observer.observe(containerRef.current)

    containerRef.current.addEventListener("scroll", handleScroll)

    return () => {
      containerRef.current &&
        observer.unobserve(containerRef.current)

      containerRef.current.removeEventListener("scroll", handleScroll)
    }
  }, [
    data,
    dataLength,
    hasMore
  ])

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ overflow: "auto" }}
    >
      {listHeaderComponent}
      {data.length !== 0
        ?
        <div
          ref={wrapperRef}
          style={{
            paddingTop: `${padding.top}px`,
            paddingBottom: `${padding.bottom}px`
          }}
        >
          {items.map((item, index) =>
            <div
              key={keyField ? item[keyField] : index}
              style={{ height: `${itemHeight}px` }}
            >
              {renderItem(item, index)}
            </div>
          )}
        </div>
        :
        listEmptyComponent
      }
      {hasMore && loader}
      {listFooterComponent}
    </div>
  )
}

export default VirtualList